import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { OnChangeFn, SortingState } from '@tanstack/react-table';
import { useCurrencies } from 'api/useCurrencies';
import useOrders from 'api/useOrders';
import { OrderDetails } from 'common/contracts';
import { PAGINATION_ROWS_PER_PAGE } from 'constants/constants';
import { SortingDirection, SortingOrderValue } from 'constants/enums';

import { OrderActions } from './Components/OrderActions';
import { OrderList } from './Components/OrderList';
import { OrdersFilterBar } from './Components/OrdersFilterBar';
import { OrderTabs } from './Components/OrderTabs';
import { useOrdersFilters } from './hooks';
import { cleanFilters, getTableSortingState } from './utils';
import { DEFAULT_SORTING } from './constant';

export const Orders = () => {
  const {
    filters,
    clearFiltersButton,
    handleClearFilters,
    selectedProjects,
    setSelectedProjects,
    handleUpdateParam,
    handleUpdateDateParams
  } = useOrdersFilters();

  const { currencyCodeToCurrencyMap } = useCurrencies();

  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage] = useState(PAGINATION_ROWS_PER_PAGE);
  const [accumulatedOrders, setAccumulatedOrders] = useState<OrderDetails[]>(
    []
  );
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const processedPageRef = useRef(-1);
  const [sorting, setSorting] = useState(DEFAULT_SORTING);

  const cleanedFilters = useMemo(() => cleanFilters(filters), [filters]);
  const cleanedFiltersKey = useMemo(
    () => JSON.stringify(cleanedFilters),
    [cleanedFilters]
  );

  const { getOrders: orders } = useOrders(
    undefined,
    { currentPage, rowsPerPage },
    sorting,
    cleanedFilters
  );

  // Reset sorting when filters change
  useEffect(() => {
    setSorting((currentSorting) => {
      if (
        currentSorting.sortValue === DEFAULT_SORTING.sortValue &&
        currentSorting.direction === DEFAULT_SORTING.direction
      ) {
        return currentSorting;
      }

      return DEFAULT_SORTING;
    });
  }, [cleanedFiltersKey]);

  // Reset pagination when filters or sorting change
  useEffect(() => {
    setCurrentPage(0);
    setAccumulatedOrders([]);
    processedPageRef.current = -1;
  }, [cleanedFiltersKey, sorting.sortValue, sorting.direction]);

  // Add new orders to accumulated list - only once per page
  useEffect(() => {
    if (!orders.data?.orders || orders.isLoading) return;
    if (processedPageRef.current === currentPage) return;

    processedPageRef.current = currentPage;

    if (currentPage === 0) {
      setAccumulatedOrders(orders.data.orders);
    } else {
      setAccumulatedOrders((prev) => {
        return [...prev, ...orders.data.orders];
      });
    }
    setIsLoadingMore(false);
  }, [orders.data?.orders, orders.isLoading, currentPage]);

  const handleLoadMore = useCallback(() => {
    if (orders.isLoading || isLoadingMore) return;

    setIsLoadingMore(true);
    setCurrentPage((prev) => prev + 1);
  }, [orders.isLoading, isLoadingMore]);

  const handleSortingChange: OnChangeFn<SortingState> = useCallback(
    (updaterOrValue) => {
      const nextSortingState =
        typeof updaterOrValue === 'function'
          ? updaterOrValue(getTableSortingState(sorting))
          : updaterOrValue;
      const dateSorting = nextSortingState.find(({ id }) => id === 'date');
      const nextDirection = dateSorting?.desc
        ? SortingDirection.DESC
        : SortingDirection.ASC;

      setSorting((currentSorting) => {
        if (dateSorting) {
          if (currentSorting.direction === nextDirection) {
            return currentSorting;
          }

          return {
            sortValue: SortingOrderValue.DATE,
            direction: nextDirection
          };
        }

        if (currentSorting.direction === SortingDirection.DESC) {
          return currentSorting;
        }

        return {
          sortValue: SortingOrderValue.DATE,
          direction: SortingDirection.DESC
        };
      });
    },
    [sorting]
  );

  const hasMore = useMemo(() => {
    if (!orders.data) return false;
    return accumulatedOrders.length < orders.data.totalCount;
  }, [orders.data, accumulatedOrders.length]);

  return (
    <div className="w-full flex flex-col gap-4 p-6 pt-4 h-[calc(100vh-50px)]">
      <div className="font-medium text-2xl text-base-primary">Orders</div>

      <OrderTabs handleUpdateParam={handleUpdateParam} />

      <OrdersFilterBar
        isFree={filters.isFree}
        clearFiltersButton={clearFiltersButton}
        handleClearFilters={handleClearFilters}
        selectedProjects={selectedProjects}
        setSelectedProjects={setSelectedProjects}
        appliedProjects={cleanedFilters.selectedProjects ?? ''}
        handleUpdateParam={handleUpdateParam}
        handleUpdateDateParams={handleUpdateDateParams}
      />

      <div className="flex flex-col gap-4 border border-base-border rounded-lg p-4 border-b-0">
        <OrderActions />
        <OrderList
          orders={accumulatedOrders}
          currencyCodeToCurrencyMap={currencyCodeToCurrencyMap}
          isLoading={orders.isLoading && currentPage === 0}
          isFree={filters.isFree}
          stickyHeader
          sortingState={getTableSortingState(sorting)}
          onSortingChange={handleSortingChange}
          infiniteScrollConfig={{
            enabled: true,
            onLoadMore: handleLoadMore,
            hasMore,
            isLoadingMore
          }}
        />
      </div>
    </div>
  );
};
